/** Per-serving nutrition map helpers (aligned with company/customer APIs). */

export const DEFAULT_FOOD_ITEM_NUTRITION = {
    protein: '0 gm',
    carbs: '0 gm',
    fat: '0 gm',
    fiber: '0 gm',
    sugar: '0 gm',
    calories: '0 kcal',
};

export const NUTRITION_FIELD_KEYS = Object.keys(DEFAULT_FOOD_ITEM_NUTRITION);

export const NUTRITION_UNIT_OPTIONS = ['gm', 'mg', 'ml', 'kcal'];

export function emptyNutritionRows() {
    return [];
}

/** @deprecated Use emptyNutritionRows — kept for callers expecting the old name. */
export function emptyNutritionForm() {
    return emptyNutritionRows();
}

export function formatNutritionLabel(key) {
    return String(key || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function pickNutritionRaw(item) {
    if (!item || typeof item !== 'object') return null;
    return item.nutrition ?? item.Nutrition ?? null;
}

export function defaultUnitForNutrient(nutrient) {
    return nutrient === 'calories' ? 'kcal' : 'gm';
}

/** Split stored value like "10 gm" into quantity and unit for the editor. */
export function parseQuantityUnit(value, defaultUnit = 'gm') {
    const str = String(value ?? '').trim();
    if (!str) {
        return { quantity: '', unit: defaultUnit };
    }
    const match = str.match(/^([\d.]+)\s*(.*)$/);
    if (match) {
        return {
            quantity: match[1],
            unit: String(match[2] || defaultUnit).trim() || defaultUnit,
        };
    }
    return { quantity: '', unit: defaultUnit };
}

/** Nutrition map from API JSON, e.g. { protein: "10 gm", carbs: "25 gm" }. */
export function resolveNutritionMap(item) {
    const raw = pickNutritionRaw(item);
    if (raw == null) {
        return { ...DEFAULT_FOOD_ITEM_NUTRITION };
    }
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) return { ...DEFAULT_FOOD_ITEM_NUTRITION };
        try {
            return resolveNutritionMap({ nutrition: JSON.parse(trimmed) });
        } catch {
            return { ...DEFAULT_FOOD_ITEM_NUTRITION };
        }
    }
    if (Array.isArray(raw)) {
        return buildNutritionMapFromRows(raw);
    }
    if (typeof raw !== 'object') {
        return { ...DEFAULT_FOOD_ITEM_NUTRITION };
    }
    const out = { ...DEFAULT_FOOD_ITEM_NUTRITION };
    Object.entries(raw).forEach(([key, val]) => {
        const label = String(key || '').trim();
        const value = val == null ? '' : String(val).trim();
        if (label && value) {
            out[label] = value;
        }
    });
    return out;
}

/** Top-level calories for tables/API compatibility. */
export function caloriesFromNutrition(item) {
    const legacy = item?.calories ?? item?.Calories;
    if (legacy != null && String(legacy).trim() !== '') {
        return String(legacy).trim();
    }
    const nutrition = resolveNutritionMap(item);
    const parsed = parseQuantityUnit(nutrition.calories, 'kcal');
    if (!parsed.quantity || isZeroNutritionValue(nutrition.calories)) {
        return '';
    }
    return parsed.unit === 'kcal' ? parsed.quantity : nutrition.calories;
}

/** True when value is numeric zero, e.g. "0", "0 gm", "0.0 g". */
export function isZeroNutritionValue(value) {
    const str = String(value ?? '').trim();
    if (!str) return true;
    const match = str.match(/^(-?\d+(?:\.\d+)?)/);
    if (!match) return false;
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) && parsed === 0;
}

function formatNutritionStoredValue(quantity, unit) {
    const qty = String(quantity ?? '').trim();
    const unitLabel = String(unit ?? 'gm').trim() || 'gm';
    if (!qty) return '';
    return `${qty} ${unitLabel}`.trim();
}

/** Convert editor rows to API nutrition map. */
export function buildNutritionMapFromRows(rows) {
    const out = { ...DEFAULT_FOOD_ITEM_NUTRITION };
    if (!Array.isArray(rows)) {
        return out;
    }
    rows.forEach((row) => {
        if (!row || typeof row !== 'object') return;
        const nutrient = String(row.nutrient ?? '').trim().toLowerCase();
        const stored = formatNutritionStoredValue(row.quantity, row.unit);
        if (nutrient && stored) {
            out[nutrient] = stored;
        }
    });
    return out;
}

/** API payload from editor rows; empty rows fall back to defaults. */
export function buildNutritionForSubmit(formNutrition) {
    if (Array.isArray(formNutrition)) {
        return buildNutritionMapFromRows(formNutrition);
    }
    const out = {};
    NUTRITION_FIELD_KEYS.forEach((key) => {
        const val = String(formNutrition?.[key] ?? '').trim();
        out[key] = val || DEFAULT_FOOD_ITEM_NUTRITION[key];
    });
    return out;
}
